import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Alert, CircularProgress
} from '@mui/material';
import {
    Notifications as NotificationsIcon
} from '@mui/icons-material';
import StateSelector from './StateSelector';
import { StatePreferenceService } from '../StatePreferenceService';

const OnboardingDialog = ({ open, onContinue }) => {
    const [selectedStates, setSelectedStates] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            fetchStateData();
        }
    }, [open]);

    const fetchStateData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const available = await StatePreferenceService.getAllAvailableStates();
            setAllStates(available);
            setSelectedStates([]);
        } catch (error) {
            console.error('Error fetching state preferences:', error);
            setError('Failed to load state preferences. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleStateChange = (newStates) => {
        setSelectedStates(newStates);
    };

    const handleSave = async () => {
        if (selectedStates.length === 0) {
            setError('Please select at least one state to receive notifications.');
            return;
        }

        setSaving(true);
        setError(null);
        
        try {
            const stateIds = selectedStates.map(s => s.stateId);
            await StatePreferenceService.updateUserStatePreferences(stateIds);
            await onContinue();
        } catch (error) {
            console.error('Error saving state preferences:', error);
            setError('Failed to save state preferences. Please try again.');
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={Boolean(open)}
            maxWidth="md"
            fullWidth
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '90vh',
                    bgcolor: 'var(--background-color-light)',
                    color: 'text.primary',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundImage: 'none',
                    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1, bgcolor: 'var(--background-color-light)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationsIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" component="span">
                        Welcome to Premium!
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 2, bgcolor: 'var(--background-color-light)' }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Typography variant="body1" paragraph>
                    Select which states you want to receive Best Buy In-Store notifications for.
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <StateSelector
                        selectedStates={selectedStates}
                        allStates={allStates}
                        onChange={handleStateChange}
                        loading={loading}
                        saving={saving}
                        hasChanges={false}
                        hideActions={true}
                    />
                )}

                {selectedStates.length === 0 && !loading && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Please select at least one state to continue.
                    </Alert>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    pb: 3,
                    pt: 2,
                    bgcolor: 'var(--background-color-light)',
                    borderTop: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', width: '100%' }}>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading || saving || selectedStates.length === 0}
                        startIcon={saving ? <CircularProgress size={20} /> : null}
                        size="large"
                    >
                        {saving ? 'Saving...' : `Save & Continue (${selectedStates.length} ${selectedStates.length === 1 ? 'state' : 'states'} selected)`}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default OnboardingDialog;
